// PayPal.me helpers. Mirrors the backend helper: the handle is the only thing
// we store (payment_methods.paypal.paypal_me); URLs are always rebuilt from it.
// Keep this file dependency-free so it can be unit-tested in isolation.

const HANDLE_PATTERN = /^[A-Za-z0-9]{1,20}$/;

export function isValidPaypalMeHandle(handle: string | null | undefined): boolean {
  return typeof handle === 'string' && HANDLE_PATTERN.test(handle);
}

/**
 * Accepts "maidmadness", "@maidmadness", "paypal.me/maidmadness" or
 * "https://www.paypal.me/maidmadness/25GBP" and returns the bare handle,
 * or null when the input is empty or not a valid handle.
 */
export function normalisePaypalMeHandle(input: string | null | undefined): string | null {
  if (typeof input !== 'string') return null;
  let value = input.trim();
  if (!value) return null;
  value = value.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  value = value.replace(/^paypal\.me\//i, '');
  value = value.replace(/^@/, '');
  // Drop any amount/path/query/hash that follows the handle.
  const handle = value.split(/[/?#]/)[0].trim();
  return isValidPaypalMeHandle(handle) ? handle : null;
}

function formatAmount(amount: number): string {
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
}

/**
 * https://paypal.me/<handle> or, with a positive amount,
 * https://paypal.me/<handle>/<amount><CURRENCY> (e.g. .../45.50GBP).
 * Returns null when the handle is invalid.
 */
export function buildPaypalMeUrl(
  handle: string | null | undefined,
  amount?: number | string | null,
  currency: string = 'GBP',
): string | null {
  const clean = normalisePaypalMeHandle(handle);
  if (!clean) return null;
  const base = `https://paypal.me/${clean}`;
  const numeric = typeof amount === 'string' ? Number(amount) : amount;
  if (numeric == null || !Number.isFinite(numeric) || numeric <= 0) return base;
  const code = /^[A-Za-z]{3}$/.test(currency) ? currency.toUpperCase() : 'GBP';
  return `${base}/${formatAmount(Math.round(numeric * 100) / 100)}${code}`;
}
